import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSaveLanguagePreference, useUserPreferences } from '../../services/conversionsApi';

/**
 * The header language toggle (fleet i18n standard §3,
 * ptc-docs/coordination/plans/2026-10-21-fleet-i18n-standard.md). Copied from ptc-statetax's
 * `LanguageToggle` (#54), the fleet reference; only the API import differs.
 *
 * The choice persists twice: per BROWSER (i18next's detector caches it in localStorage, so the page
 * paints in it before sign-in) and per PERSON (the `language` preference, so it follows them to
 * another machine). A saved choice is applied ONCE, when the preferences arrive after sign-in; after
 * that the toggle owns it, and a choice made before they arrive is never overridden by them.
 *
 * There is no default (ruled 2026-10-21): a person who has never toggled has `language: null`, and
 * nothing here invents one.
 */
export default function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const { data: prefs } = useUserPreferences();
  const { mutate: save } = useSaveLanguagePreference();
  const settled = useRef(false);

  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es') ? 'es' : 'en';
  const next = current === 'es' ? 'en' : 'es';

  useEffect(() => {
    if (settled.current || !prefs) return;
    settled.current = true;
    const saved = prefs.language;
    if ((saved === 'en' || saved === 'es') && saved !== current) void i18n.changeLanguage(saved);
  }, [prefs, current, i18n]);

  return (
    <button
      type="button"
      className="lang-toggle"
      onClick={() => {
        settled.current = true;
        void i18n.changeLanguage(next);
        // A failed save leaves this browser's choice in place; the next toggle saves again.
        save(next);
      }}
      aria-label={t('common.language.switchTo')}
      title={t('common.language.switchTo')}
    >
      {current === 'es' ? t('common.language.es') : t('common.language.en')}
    </button>
  );
}
